// InvitationsSection.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical, faTimesCircle, faClock } from '@fortawesome/free-solid-svg-icons';
import { useApplication } from './hooks/useApplication';
import { applicationApi } from './api/applicationApi';
import DeclineInvitationModal from './DeclineInvitationModal';



// ---------------------------------------------------------------------------
// Модалка подовження терміну запрошення
// ---------------------------------------------------------------------------
const ProlongModal = ({ invitation, onConfirm, onClose, loading }) => {
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  const defaultDate = new Date();
  
  if(defaultDate.getMonth() > 7 || defaultDate.getMonth() < 3) defaultDate.setDate(defaultDate.getDate() + 10);
  if(defaultDate.getMonth() < 7 && defaultDate.getMonth() > 2) {
    defaultDate.setDate(15);
    defaultDate.setMonth(7);
  }
  
  const defaultDateStr = defaultDate.toISOString().split('T')[0];

  const [newDate, setNewDate] = useState(defaultDateStr);

  return (
    <div className="modal-overlay" style={{ zIndex: 1060 }} onClick={onClose}>
      <div
        className="application-modal"
        style={{ maxWidth: 440 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">Подовження терміну запрошення</h2>
          <button className="close-button" onClick={onClose} disabled={loading}>×</button>
        </div>

        <div className="modal-content p-3">
          {invitation && (
            <div className="mb-3 text-muted" style={{ fontSize: '0.9em' }}>
              Поточний термін:{' '}
              <strong>
                {invitation.dateTo
                  ? new Date(invitation.dateTo).toLocaleDateString('uk-UA')
                  : '—'}
              </strong>
            </div>
          )}
          <label className="form-label fw-semibold">Новий термін дії запрошення</label>
          <input
            type="date"
            className="form-control"
            value={newDate}
            min={minDateStr}
            onChange={(e) => setNewDate(e.target.value)}
            disabled={loading}
          />
          {newDate && new Date(newDate) <= new Date() && (
            <div className="text-danger small mt-1">Дата має бути в майбутньому</div>
          )}
        </div>

        <div className="modal-actions">
          <div className="action-buttons">
            <button
              className="btn btn-primary"
              onClick={() => onConfirm(newDate)}
              disabled={loading || !newDate || new Date(newDate) <= new Date()}
            >
              {loading ? 'Збереження...' : 'Подовжити'}
            </button>
          </div>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Скасувати
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// InvitationsSection
// ---------------------------------------------------------------------------
const InvitationsSection = ({ applicationData, mode, applicationId, onOpenDeclineModal }) => {
  const {
    declineInvitation,
    statuses,
    statusesLoading,
    statusesError,
    fetchApplicationStatuses,
  } = useApplication(applicationId);

  const [activeMenuIndex,    setActiveMenuIndex]    = useState(null);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isProlongModalOpen, setIsProlongModalOpen] = useState(false);
  const [prolongLoading,     setProlongLoading]     = useState(false);
  const [loading,            setLoading]            = useState(false);
  const [message,            setMessage]            = useState(null);

  useEffect(() => {
    if (applicationId) fetchApplicationStatuses();
  }, [applicationId, fetchApplicationStatuses]);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const invitationStatuses = statuses?.filter(item =>
    ['invite-sent', 'invite-accepted', 'invite-declined'].includes(item.name)
  ) || [];

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('uk-UA');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('uk-UA');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'invite-sent':     { class: 'status-pending', label: 'Надіслано' },
      'invite-accepted': { class: 'status-success', label: 'Прийнято' },
      'invite-declined': { class: 'status-error',   label: 'Відхилено' },
    };
    const config = statusConfig[status] || statusConfig['invite-sent'];
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const handleMenuToggle = (index, e) => {
    e.stopPropagation();
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => setActiveMenuIndex(null);

  const handleActionSelect = (actionId, invitation) => {
    setActiveMenuIndex(null);
    setSelectedInvitation(invitation);

    if (actionId === 'decline') {
      if (onOpenDeclineModal) {
        onOpenDeclineModal(
          (reason) => handleDeclineInvitation(reason, invitation),
          invitation
        );
      } else {
        setIsDeclineModalOpen(true);
      }
    }

    if (actionId === 'prolong') {
      setIsProlongModalOpen(true);
    }
  };

  const handleDeclineInvitation = async (reason, invitation = selectedInvitation) => {
    try {
      setLoading(true);
      await declineInvitation(reason, invitation.kindergartenId, invitation.groupNumber);
      setIsDeclineModalOpen(false);
      setSelectedInvitation(null);
      fetchApplicationStatuses();
    } catch (err) {
      console.error('Error declining invitation:', err);
      alert('Помилка при відхиленні запрошення');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleProlong = async (newDateTo) => {
    try {
      setProlongLoading(true);
      await applicationApi.prolongInvite(applicationId, newDateTo);
      setIsProlongModalOpen(false);
      setSelectedInvitation(null);
      showMsg('success', `Термін запрошення подовжено до ${formatDate(newDateTo)}`);
      fetchApplicationStatuses();
    } catch (err) {
      console.error('Error prolonging invitation:', err);
      showMsg('danger', 'Помилка подовження терміну: ' + (err?.message || ''));
    } finally {
      setProlongLoading(false);
    }
  };

  // Показувати меню дій тільки для активних запрошень
  const isInvitationActive = (invitation) =>
    ['invite-sent', 'invite-accepted'].includes(invitation.name);

  const currentStatusName =
    applicationData.currentStatus?.name ?? applicationData.currentStatus;

  const canActOnInvitation = (i) =>
    (i === 0) && (mode === 'edit') &&
    ['invite-sent', 'invite-accepted', 'invite-accept'].includes(currentStatusName);

  const ActionsMenu = ({ invitation, onActionSelect, onClose }) => (
    <div className="actions-dropdown">
      {/* Подовжити */}
      <button
        className="dropdown-item d-flex align-items-center gap-2 p-2"
        onClick={() => onActionSelect('prolong', invitation)}
        style={{ border: 'none', backgroundColor: 'transparent', width: '100%', textAlign: 'left', fontSize: '14px', cursor: 'pointer' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <FontAwesomeIcon icon={faClock} style={{ width: '16px', color: '#0d6efd' }} />
        <span style={{ color: '#212529' }}>Подовжити термін</span>
      </button>

      {/* Відхилити */}
      <button
        className="dropdown-item d-flex align-items-center gap-2 p-2"
        onClick={() => onActionSelect('decline', invitation)}
        style={{ border: 'none', backgroundColor: 'transparent', width: '100%', textAlign: 'left', fontSize: '14px', cursor: 'pointer' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <FontAwesomeIcon icon={faTimesCircle} style={{ width: '16px', color: '#dc3545' }} />
        <span style={{ color: '#212529' }}>Відхилити</span>
      </button>

      <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
        <button className="btn btn-sm btn-outline-secondary w-100" onClick={onClose} style={{ fontSize: '12px' }}>
          Скасувати
        </button>
      </div>
    </div>
  );

  if (statusesLoading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Завантаження...</span>
        </div>
        <p className="mt-2">Завантаження запрошень...</p>
      </div>
    );
  }

  if (statusesError) {
    return (
      <div className="alert alert-danger m-3">
        <strong>Помилка завантаження запрошень:</strong> {statusesError}
        <div className="mt-2">
          <button className="btn btn-sm btn-outline-danger" onClick={fetchApplicationStatuses}>
            Спробувати знову
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="invitations-section">
      <h3 className="section-title">Надіслані запрошення</h3>

      {message && (
        <div className={`alert alert-${message.type} py-2 px-3 mb-3`}>
          {message.text}
        </div>
      )}

      <div className="table-responsive">
        <table className="history-table">
          <thead>
            <tr>
              <th style={{ width: '18%' }}>Дата надсилання</th>
              <th style={{ width: '13%' }}>Статус</th>
              <th style={{ width: '18%' }}>Термін дії</th>
              <th style={{ width: '25%' }}>Заклад</th>
              <th style={{ width: '15%' }}>Група</th>
              {canActOnInvitation && <th style={{ width: '11%' }}></th>}
            </tr>
          </thead>
          <tbody>
            {invitationStatuses.sort((a, b) => new Date(b.date) - new Date(a.date)).map((invitation, i) => {
              const index = 'i_' + i;
              return (
                <tr key={invitation._id || index}>
                  <td>{formatDateTime(invitation.date)}</td>
                  <td>{getStatusBadge(invitation.name)}</td>
                  <td>
                    {invitation.dateTo ? (
                      <span>
                        {formatDate(invitation.dateTo)}
                        {new Date(invitation.dateTo) < new Date() && (
                          <span className="text-danger ms-1" style={{ fontSize: '0.8em' }}>
                            (минув)
                          </span>
                        )}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    {invitation.kindergartenName
                      ? `${invitation.kindergartenName} (${invitation.kindergartenNumber})`
                      : invitation.kindergartenId}
                    {invitation.kindergartenDistrict && (
                      <div><small className="text-muted">{invitation.kindergartenDistrict}</small></div>
                    )}
                  </td>
                  <td>{invitation.groupNumber}</td>

                  {canActOnInvitation(i) && (
                    <td className="text-end">
                      {isInvitationActive(invitation) && (
                        <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                          <button
                            className="btn btn-sm btn-link text-decoration-none p-0"
                            onClick={(e) => handleMenuToggle(index, e)}
                            style={{ color: '#0d6efd' }}
                          >
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                          </button>
                          { (activeMenuIndex === index) && (
                            <ActionsMenu
                              invitation={invitation}
                              onActionSelect={handleActionSelect}
                              onClose={handleMenuClose}
                            />
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {invitationStatuses.length === 0 && (
        <div className="no-data">Запрошення відсутні</div>
      )}

      {/* Модалка подовження */}
      {isProlongModalOpen && (
        <ProlongModal
          invitation={selectedInvitation}
          onConfirm={handleProlong}
          onClose={() => { setIsProlongModalOpen(false); setSelectedInvitation(null); }}
          loading={prolongLoading}
        />
      )}

      {/* Модалка відхилення (локальна) */}
      {!onOpenDeclineModal && (
        <DeclineInvitationModal
          isOpen={isDeclineModalOpen}
          onClose={() => { setIsDeclineModalOpen(false); setSelectedInvitation(null); }}
          onDecline={handleDeclineInvitation}
          loading={loading}
          invitation={selectedInvitation}
          applicationData={applicationData}
        />
      )}
    </div>
  );
};

export default InvitationsSection;